/**
 * The one way to read the running trend switch, `ConfigKey.RunningTrendCharts` (#6627).
 *
 * Every chart that draws the running trend line and its 30/70 tunnel goes through these two
 * functions, so "on" means the same thing everywhere: only an explicit `true`. Anything else
 * (unset, `false`, a string, a config that failed to load) is off, and off is exactly the charts
 * as they were before #6627.
 *
 * Like the coaching dashboard switch, the value is resolved for the SIGNED-IN account: while a
 * coach manages a client that is the coach, and under admin impersonation it is the admin.
 * See design-docs/running-trend.md, "Where it is drawn".
 */

import { ConfigKey } from './config-keys';
import { CoachingDashboardFlagSource } from './coaching-dashboard-flag';

/** the two config-service calls the helper needs; `ConfigDbService` satisfies it */
export type RunningTrendFlagSource = CoachingDashboardFlagSource;

/** true only for an exact `true` */
export function isRunningTrendChartsValueOn(value: unknown): boolean {
  return value === true;
}

/**
 * Whether the running trend charts are on for the signed-in account. Resolves once the config has
 * loaded. A page awaits this before its first draw, so the first chart is already in the right state.
 */
export async function isRunningTrendChartsOnAsync(configDb: RunningTrendFlagSource): Promise<boolean> {
  return isRunningTrendChartsValueOn(await configDb.getConfigValueAsync<unknown>(ConfigKey.RunningTrendCharts));
}

/**
 * Calls `onChange` whenever the switch changes after the first read. Returns the function that
 * stops watching; call it when the page is destroyed.
 */
export function watchRunningTrendCharts(configDb: RunningTrendFlagSource, onChange: (isOn: boolean) => void): () => void {
  return configDb.watchConfigValue<unknown>(ConfigKey.RunningTrendCharts, value => onChange(isRunningTrendChartsValueOn(value)));
}
